/**
 * dispatchBack — translates a BackAction into a store mutation and a
 * react-router navigate() side effect.
 *
 * Source: docs/12_NAVIGATION_ACTIVITY_MODEL.md decision D4.
 *
 * This is the only place where the activity store and React Router meet.
 * Every back press (hardware, browser, in-app, close-button) flows through
 * the back resolver and ends here.
 *
 * Note: This module exposes a hook (useBackDispatcher) for in-render use,
 * plus a lower-level dispatchBackAction(action, navigate) for places that
 * already have a navigate function (e.g. Capacitor backButton listener
 * registered outside React).
 */

import { useCallback } from "react";
import { useLocation, useNavigate, type NavigateFunction } from "react-router-dom";
import { useActivityStore } from "../store/activityStore";
import { useAuthStore } from "../store/authStore";
import { useExitConfirm } from "../store/exitConfirmStore";
import { resolveBack } from "./resolveBack";
import type { BackAction, BackSource, SubActivity } from "./types";

/**
 * 활동 kind 별 close-guard 이벤트 매핑.
 *
 * back-press 로 활동이 닫히려 할 때, 페이지가 추가 확인(컨펌 모달, 큐 해제 등)
 * 을 수행해야 하면 여기에 등록한다. dispatchBack 의 close-activity 분기가
 * close 를 실행하기 전에 이 이벤트를 발사하고 close 는 페이지가 직접
 * `activityNav.close()` 로 진행한다.
 *
 * 페이지에서 `activityNav.close()` 직접 호출은 dispatchBack 을 거치지 않으므로
 * 가드가 다시 트리거되지 않는다 (무한 루프 X).
 */
const CLOSE_GUARD_EVENTS: Record<string, string> = {
  "kuji/reserve": "template:request-kuji-reserve-close",
  // 스캔: 촬영 후 라벨링 화면에서의 뒤로가기는 activity 를 닫는 대신 "다시 찍기" 로
  // 되돌린다 — 한 단계 되돌리려던 사용자가 메뉴까지 튕기던 문제 (실기기 제보).
  // 라이브 상태에서는 페이지가 그대로 close 를 진행한다.
  scan: "peeka:request-scan-close",
};

/**
 * RR v6 BrowserHistory 는 history.state 를 `{ usr, key, idx }` 로 래핑한다.
 * useDialogBackBinding 과 동일한 패턴으로 usr 만 꺼내 우리의 nav meta 를 읽는다.
 * MemoryRouter 환경(테스트)에서는 window.history.state 가 RR 의 메모리 history
 * 와 무관하므로 호출자가 location.state 를 직접 넘긴다 — 그 값이 있으면 우선 사용.
 */
export function readNavMeta(
  fallbackLocationState: unknown,
): { __activityId?: string; __entryId?: string } | null {
  const fromArg =
    typeof fallbackLocationState === "object" && fallbackLocationState !== null
      ? (fallbackLocationState as { __activityId?: string; __entryId?: string })
      : null;
  if (fromArg && typeof fromArg.__activityId === "string") return fromArg;

  const hs =
    typeof window !== "undefined" ? (window.history.state as unknown) : null;
  if (typeof hs !== "object" || hs === null) return null;
  const wrapped = hs as { usr?: unknown };
  const usr = "usr" in wrapped ? wrapped.usr : hs;
  if (typeof usr !== "object" || usr === null) return null;
  return usr as { __activityId?: string; __entryId?: string };
}

/**
 * Close one SubActivity and navigate to whatever comes next.
 *
 * 두 가지 경로:
 *   (a) in-app open() 경로 — 브라우저 history 에 우리가 push 한 항목이
 *       있으면 navigate(-N) 으로 N 개 pop 한다. 그러면 popstate → RR location
 *       업데이트 → usePopstateSync.reconcileFromUrl 이 store 를 정리한다.
 *       이 흐름은 history 에 잔재를 남기지 않는다.
 *
 *   (b) deep-link / 새로고침 / 외부 진입 — history.state 에 우리의 __activityId
 *       가 없거나 다른 활동의 것이면 pop 할 수 있는 history 가 없다고 보고
 *       기존처럼 navigate(targetUrl, replace) + store mutate 로 처리한다.
 *
 * Gate 조건:
 *   - closing 이 stack 의 top activity 여야 한다 (non-top close 는 흔치 않은
 *     경로이고 history 매핑이 모호하므로 안전하게 replace fallback).
 *   - history.state.__activityId === closing.id (현재 history 위치를 우리가 push 한 것).
 */
export function closeActivityAndNavigate(
  activityId: string | undefined,
  navigate: NavigateFunction,
  currentLocationState?: unknown,
): void {
  const stack = useActivityStore.getState();
  const closing =
    activityId === undefined
      ? stack.activities[stack.activities.length - 1]
      : stack.activities.find((a) => a.id === activityId);

  if (!closing) return;

  const fallbackUrl = closing.fallbackPath ?? "/";

  // 인증 안 된 상태로 auth activity 를 닫는 경우 — 두 진입 경로를 구분한다.
  //
  //   (1) RequireAuth 보호 페이지(예: /me/box)에서 열린 auth:
  //       그 페이지로 돌아가면 RequireAuth 가 다시 auth 를 열어 무한 루프.
  //       → 스택 전체 클리어 + fallbackPath("/" = AUTH_ACTIVITY_FALLBACK_PATH)로 이동.
  //
  //   (2) 공개 페이지의 액션(예: 쿠지 상세의 "참여하기")에서 열린 auth:
  //       이때 auth.fallbackPath 는 그 공개 페이지의 URL(예: /shop/18)이고, 그
  //       페이지 활동이 바로 아래에 살아있다. 여기서 closeAll 하면 공개 상세까지
  //       날아가고 useActivityHydration 이 재생성 → history/스택 desync → 뒤로가기
  //       시 흰 화면 회귀가 난다. 그러므로 스택을 비우지 말고 아래의 일반 close
  //       경로(history-pop)로 흘려보내 auth 만 정리하고 공개 상세를 그대로 유지한다.
  //
  // 구분 기준: auth.fallbackPath 가 바로 아래 활동의 현재 URL 과 같으면 (2) 케이스.
  // RequireAuth 케이스는 fallbackPath 가 "/" 라 이 조건에 걸리지 않는다.
  if (closing.kind === "auth" && !useAuthStore.getState().accessToken) {
    const closingIdx = stack.activities.findIndex((a) => a.id === closing.id);
    const underlying = closingIdx > 0 ? stack.activities[closingIdx - 1] : null;
    const underlyingUrl = underlying?.entries.slice(-1)[0]?.url;
    const openedOverPublicUnderlying =
      !!underlying && underlyingUrl !== undefined && underlyingUrl === fallbackUrl;
    if (!openedOverPublicUnderlying) {
      navigate(fallbackUrl, { replace: true });
      stack.closeAll();
      return;
    }
    // (2) 케이스 — 아래의 일반 close 경로로 진행 (history-pop → reconcileFromUrl).
  }

  const topActivityId = stack.activities[stack.activities.length - 1]?.id;
  const isTopActivity = topActivityId === closing.id;

  // Non-top close — 위에 다른 활동이 있는 상태에서 아래 활동을 명시적으로 닫는
  // 경로. 현재 URL/history 는 top 활동의 것이므로 navigate 하지 말고 store 만
  // 정리한다. (만지면 위 활동의 URL 이 어긋난다.)
  if (!isTopActivity) {
    stack.closeActivity(closing.id);
    return;
  }

  const meta = readNavMeta(currentLocationState);
  const canPopHistory = meta?.__activityId === closing.id;

  // historyIsolated 액티비티(closeAll*/closeAboveKind* 위에 replace 로 열림)는 그 아래
  // 브라우저 history 가 store 와 어긋나 있다(stale funnel 항목 잔존). navigate(-N) 하면
  // detail 이 아니라 stale entry 로 오버슈트 → reconcileFromUrl 이 스택을 비운다. 그러므로
  // history-pop 을 건너뛰고 아래의 replace-to-underlying 경로로 닫는다(detail 로 자연 복귀).
  if (canPopHistory && !closing.historyIsolated) {
    // history 의 N 개 항목을 pop 한다. browser back 과 동일한 경로 (popstate →
    // usePopstateSync.reconcileFromUrl) 가 store 를 정리하므로 여기서는 직접
    // store 를 mutate 하지 않는다. 직접 mutate 하면 popstate 가 도착하기 전
    // 한 render 사이 isOverlay=false + URL=closing URL 의 mismatch 가 생긴다.
    navigate(-closing.entries.length);
    return;
  }

  // Deep-link / hydrated case: pop 할 history 가 없으므로 replace 로 fallback.
  //
  // Capture underlying BEFORE mutating, so a "close-top with one underlying"
  // resolves to the underlying's URL, not whatever happens to be top after
  // the mutation.
  const closingIdx = stack.activities.findIndex((a) => a.id === closing.id);
  const underlying =
    closingIdx > 0 ? stack.activities[closingIdx - 1] : null;
  const targetUrl = underlying?.entries.slice(-1)[0]?.url ?? fallbackUrl;

  // Call navigate() BEFORE the store mutation so that the next render sees a
  // consistent snapshot: location.pathname matches the underlying URL and the
  // store has no closing activity. If the store mutates first, Layout briefly
  // renders with isOverlay=false while location still points at the activity's
  // URL — causing backgroundOutletRef to capture the closing activity's outlet
  // and re-mount the underlying page (docs/12 Phase 2C-full freeze regression).
  navigate(targetUrl, { replace: true });
  stack.closeActivity(closing.id);
}

/**
 * Apply a BackAction. Mutates the store; if the action requires a URL
 * change, calls navigate() with the appropriate options.
 *
 * Inputs:
 *   - action:        the resolved BackAction (from resolveBack).
 *   - navigate:      react-router navigate function.
 *   - underlying:    UNUSED in current implementation — retained for backward
 *                    compatibility with existing callers. closeActivityAndNavigate
 *                    looks up the underlying from the live store.
 *   - currentLocationState: 현재 RR location.state. close-activity / pop-entry 가
 *                    history 의 현재 항목이 우리가 push 한 것인지 판정할 때 사용.
 *                    없으면 window.history.state 로 fallback (BrowserRouter 전용).
 */
export function dispatchBackAction(
  action: BackAction,
  navigate: NavigateFunction,
  _underlying: SubActivity | null,
  currentLocationState?: unknown,
): void {
  const store = useActivityStore.getState();

  switch (action.type) {
    case "close-dialog":
      store.closeDialog(action.dialogId);
      // Dialogs don't change URL — no navigate.
      return;

    case "pop-entry": {
      // history 의 현재 항목을 우리가 push 한 것 (해당 activity 의 어떤 entry)
      // 인지 확인. 맞으면 navigate(-1) → popstate → usePopstateSync.reconcileFromUrl
      // 이 store 의 top entry 를 pop. history 잔재가 남지 않는다.
      const meta = readNavMeta(currentLocationState);
      if (meta?.__activityId === action.activityId) {
        navigate(-1);
        return;
      }
      // Deep-link / stale state: store 만 pop 하고 URL 은 replace 로 맞춤.
      store.popEntry(action.activityId);
      const top = useActivityStore.getState().activities.slice(-1)[0];
      const topEntry = top?.entries.slice(-1)[0];
      if (topEntry) {
        navigate(topEntry.url, { replace: true, state: topEntry.state });
      }
      return;
    }

    case "close-activity": {
      // 페이지가 close 직전 추가 처리(컨펌, 큐 해제 등)를 해야 하면 close 를
      // 보류하고 이벤트만 발사. 페이지가 결정 후 직접 activityNav.close() 호출.
      const closing = useActivityStore
        .getState()
        .activities.find((a) => a.id === action.activityId);
      const guardEvent = closing ? CLOSE_GUARD_EVENTS[closing.kind] : undefined;
      if (guardEvent) {
        window.dispatchEvent(new CustomEvent(guardEvent));
        return;
      }
      closeActivityAndNavigate(action.activityId, navigate, currentLocationState);
      return;
    }

    case "main-tab-back":
      navigate("/", { replace: true });
      return;

    case "exit-app":
      // 홈에서 뒤로가기 → 즉시 종료하지 않고 컨펌 다이얼로그를 띄운다.
      // 다이얼로그가 열려있는 동안 activityStore.dialogs 의 top 을 점유하므로
      // 같은 백 한 번 더 = 다이얼로그 닫기 (앱 종료 X).
      useExitConfirm.getState().open();
      return;

    case "noop":
      return;
  }
}

/**
 * Hook for components that need a back dispatcher.
 * Wraps resolveBack + dispatchBackAction with the current location and
 * a stable callback identity.
 */
export function useBackDispatcher(): (source: BackSource) => void {
  const navigate = useNavigate();
  const location = useLocation();

  return useCallback(
    (source: BackSource) => {
      const stack = useActivityStore.getState();
      const action = resolveBack({
        stack: { activities: stack.activities, dialogs: stack.dialogs },
        source,
        mainPathname: location.pathname,
      });

      // Capture the underlying activity (one below top) before the action
      // mutates the store, so close-activity can navigate to its URL.
      const underlying =
        stack.activities.length >= 2
          ? stack.activities[stack.activities.length - 2]
          : null;

      dispatchBackAction(action, navigate, underlying, location.state);
    },
    [navigate, location.pathname, location.state],
  );
}
